/**
 * admin/src/pages/UsersPage.tsx
 * Quản lý tài khoản phụ huynh v3.0
 * Thêm: Bulk select, Bulk tier change, Export CSV
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUsers, useUserMutations } from '../hooks/useUsers';
import { adminFetch } from '../hooks/useAdmin';
import {
  DataTable,
  TierBadge,
  Pagination,
  SearchInput,
  Modal,
  ErrorBanner,
  toast,
  formatDate,
  timeAgo,
} from '../components/ui/index';
import type { Column } from '../components/ui/index';
import type { AdminUserRow, SubscriptionTier } from '../types/admin';

// ─── Constants ────────────────────────────────────────────────────────────────

const TIER_OPTIONS: { value: SubscriptionTier | ''; label: string }[] = [
  { value: '',        label: 'Tất cả tier' },
  { value: 'member',  label: '👤 Member' },
  { value: 'premium', label: '💜 Premium' },
];

const PREMIUM_DURATIONS = [
  { days: 30,  label: '1 tháng (30 ngày)' },
  { days: 90,  label: '3 tháng (90 ngày)' },
  { days: 180, label: '6 tháng (180 ngày)' },
  { days: 365, label: '1 năm (365 ngày)' },
];

const PAGE_SIZE = 20;

// ─── CSV Export ───────────────────────────────────────────────────────────────

function exportUsersCSV(users: AdminUserRow[]) {
  const headers = ['Email', 'Tên', 'Tier', 'Học sinh', 'Ngày đăng ký'];
  const rows = users.map(u => [
    `"${u.email}"`,
    `"${u.name ?? ''}"`,
    u.subscription_tier,
    u.student_count,
    formatDate(u.created_at),
  ]);
  const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast(`Đã xuất ${users.length} users`, 'success');
}

// ─── Bulk Action Bar ──────────────────────────────────────────────────────────

function BulkBar({
  count,
  onBulkTier,
  onClear,
}: {
  count: number;
  onBulkTier: () => void;
  onClear: () => void;
}) {
  if (count === 0) return null;
  return (
    <div style={{
      position: 'fixed',
      bottom: 32,
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-focus)',
      borderRadius: 'var(--radius-xl)',
      padding: '10px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      boxShadow: 'var(--shadow-xl)',
      zIndex: 150,
      animation: 'slideUp 0.2s ease',
      backdropFilter: 'blur(12px)',
    }}>
      <span style={{ fontWeight: 700, color: 'var(--accent-light)', fontSize: 14 }}>
        ✓ {count} users được chọn
      </span>
      <div style={{ width: 1, height: 20, background: 'var(--border)' }} />
      <button className="btn btn-secondary btn-sm" onClick={onBulkTier}>
        ✏️ Đổi tier hàng loạt
      </button>
      <button className="btn btn-ghost btn-sm" onClick={onClear}>
        Bỏ chọn
      </button>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function UsersPage() {
  // ── Filter / pagination ─────────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<SubscriptionTier | ''>('');
  const [page, setPage] = useState(1);

  // ── Single user modal ───────────────────────────────────────────────────────
  const [showChangeTierModal, setShowChangeTierModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUserRow | null>(null);
  const [newTier, setNewTier] = useState<SubscriptionTier>('member');
  const [durationDays, setDurationDays] = useState(30);

  // ── Bulk select ─────────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkTier, setBulkTier] = useState<SubscriptionTier>('member');
  const [bulkDays, setBulkDays] = useState(30);
  const [bulkSaving, setBulkSaving] = useState(false);

  // ── Debounce search ─────────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => { setPage(1); setSelectedIds(new Set()); }, [tierFilter]);

  // ── Data ────────────────────────────────────────────────────────────────────
  const { users: userRows, total, totalPages, isLoading, error, refetch } = useUsers({
    page, limit: PAGE_SIZE,
    search: debouncedSearch || undefined,
    tier: tierFilter || undefined,
  });

  const { setPremium, setMember, isSaving } = useUserMutations(refetch);

  // ── Single tier change ──────────────────────────────────────────────────────
  function openChangeTier(user: AdminUserRow) {
    setSelectedUser(user);
    setNewTier(user.subscription_tier);
    setDurationDays(30);
    setShowChangeTierModal(true);
  }

  function closeModal() { setShowChangeTierModal(false); setSelectedUser(null); }

  async function handleConfirmChangeTier() {
    if (!selectedUser) return;
    if (newTier === 'premium') await setPremium(selectedUser.id, durationDays);
    else await setMember(selectedUser.id);
    closeModal();
  }

  // ── Bulk select helpers ─────────────────────────────────────────────────────
  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === userRows.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(userRows.map(u => u.id)));
    }
  }

  // ── Bulk tier change ────────────────────────────────────────────────────────
  async function handleBulkTier() {
    setBulkSaving(true);
    const ids = Array.from(selectedIds);
    try {
      await Promise.all(
        ids.map(id => {
          if (bulkTier === 'premium') return adminFetch('/subscriptions', { method: 'POST', body: JSON.stringify({ user_id: id, plan: 'premium', days: bulkDays }) });
          return adminFetch(`/users/${id}`, { method: 'PATCH', body: JSON.stringify({ tier: 'member' }) });
        })
      );
      toast(`Đã đổi ${ids.length} users → ${bulkTier}`, 'success');
      setSelectedIds(new Set());
      setShowBulkModal(false);
      refetch();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Lỗi đổi tier hàng loạt', 'error');
    } finally {
      setBulkSaving(false);
    }
  }

  // ── Table columns ───────────────────────────────────────────────────────────
  const allOnPageSelected = userRows.length > 0 && userRows.every(u => selectedIds.has(u.id));
  const someSelected = selectedIds.size > 0 && !allOnPageSelected;

  const userColumns: Column<AdminUserRow>[] = [
    {
      key: '_select',
      label: '',
      width: '44px',
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedIds.has(row.id)}
          onChange={() => toggleSelect(row.id)}
          onClick={e => e.stopPropagation()}
          style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--accent)' }}
        />
      ),
    },
    {
      key: 'email',
      label: 'Email',
      render: (row) => (
        <div>
          <div className="cell-primary">{row.email}</div>
          {row.name && <div className="cell-muted">{row.name}</div>}
        </div>
      ),
    },
    {
      key: 'subscription_tier',
      label: 'Tier',
      width: '100px',
      render: (row) => <TierBadge tier={row.subscription_tier} />,
    },
    {
      key: 'student_count',
      label: '🧒 Học sinh',
      width: '100px',
      render: (row) => <span style={{ fontWeight: 600 }}>{row.student_count}</span>,
    },
    {
      key: 'created_at',
      label: 'Ngày đăng ký',
      width: '140px',
      render: (row) => (
        <div>
          <div>{formatDate(row.created_at)}</div>
          <div className="cell-muted">{timeAgo(row.created_at)}</div>
        </div>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '170px',
      render: (row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Link to={`/admin/users/${row.id}`} className="btn btn-ghost btn-sm" onClick={e => e.stopPropagation()}>
            👁 Xem
          </Link>
          <button
            className="btn btn-secondary btn-sm"
            onClick={e => { e.stopPropagation(); openChangeTier(row); }}
          >
            ✏️ Tier
          </button>
        </div>
      ),
    },
  ];

  // Override header checkbox với render trực tiếp ở toolbar
  const columnsWithCheck = userColumns;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header__left">
          <h1>Users</h1>
          <p>Quản lý tài khoản phụ huynh · {total.toLocaleString('vi-VN')} tài khoản</p>
        </div>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => exportUsersCSV(userRows)}
          disabled={userRows.length === 0}
          title="Xuất trang hiện tại thành CSV"
        >
          ⬇ Export CSV
        </button>
      </div>

      {error && <div style={{ marginBottom: 16 }}><ErrorBanner message={error} onRetry={refetch} /></div>}

      <div className="card">
        {/* Toolbar */}
        <div className="table-toolbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, flexWrap: 'wrap' }}>
            {/* Select all checkbox */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={allOnPageSelected}
                ref={el => { if (el) el.indeterminate = someSelected; }}
                onChange={toggleSelectAll}
                style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--accent)' }}
                title="Chọn tất cả trang này"
              />
            </div>
            <SearchInput value={searchInput} onChange={setSearchInput} placeholder="Tìm email, tên..." />
            <select
              className="input"
              value={tierFilter}
              onChange={e => setTierFilter(e.target.value as SubscriptionTier | '')}
              style={{ minWidth: 140 }}
            >
              {TIER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <span className="cell-muted" style={{ fontSize: 13, flexShrink: 0 }}>
            {total.toLocaleString('vi-VN')} users
          </span>
        </div>

        <div className="table-wrapper">
          <DataTable<Record<string, unknown>>
            columns={columnsWithCheck as unknown as Column<Record<string, unknown>>[]}
            rows={userRows as unknown as Record<string, unknown>[]}
            isLoading={isLoading}
            emptyText="Không tìm thấy user nào"
          />
        </div>

        {!isLoading && (
          <Pagination
            page={page} totalPages={totalPages} totalRows={total} pageSize={PAGE_SIZE}
            onPageChange={setPage} hasPrev={page > 1} hasNext={page < totalPages}
          />
        )}
      </div>

      {/* ── Bulk Action Bar ── */}
      <BulkBar
        count={selectedIds.size}
        onBulkTier={() => setShowBulkModal(true)}
        onClear={() => setSelectedIds(new Set())}
      />

      {/* ── Single Change Tier Modal ── */}
      <Modal
        isOpen={showChangeTierModal}
        onClose={closeModal}
        title={`Đổi tier: ${selectedUser?.email ?? ''}`}
        actions={
          <>
            <button className="btn btn-secondary" onClick={closeModal} disabled={isSaving}>Huỷ</button>
            <button
              className="btn btn-primary"
              onClick={handleConfirmChangeTier}
              disabled={isSaving || newTier === selectedUser?.subscription_tier}
            >
              {isSaving ? 'Đang lưu...' : 'Xác nhận'}
            </button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">Tier mới</label>
          <select className="input" value={newTier} onChange={e => { setNewTier(e.target.value as SubscriptionTier); if (e.target.value === 'premium') setDurationDays(30); }} style={{ width: '100%' }}>
            <option value="member">👤 Member</option>
            <option value="premium">💜 Premium</option>
          </select>
        </div>
        {newTier === 'premium' && (
          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label">⏳ Thời hạn Premium</label>
            <select className="input" value={durationDays} onChange={e => setDurationDays(Number(e.target.value))} style={{ width: '100%' }}>
              {PREMIUM_DURATIONS.map(opt => <option key={opt.days} value={opt.days}>{opt.label}</option>)}
            </select>
          </div>
        )}
        {selectedUser && (
          <p className="cell-muted" style={{ marginTop: 12, fontSize: 13 }}>
            Hiện tại: <strong>{selectedUser.subscription_tier}</strong>
            {newTier !== selectedUser.subscription_tier && (
              <> → <strong>{newTier}{newTier === 'premium' ? ` (${durationDays} ngày)` : ''}</strong></>
            )}
          </p>
        )}
      </Modal>

      {/* ── Bulk Tier Modal ── */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => !bulkSaving && setShowBulkModal(false)}
        title={`Đổi tier hàng loạt (${selectedIds.size} users)`}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => setShowBulkModal(false)} disabled={bulkSaving}>Huỷ</button>
            <button className="btn btn-primary" onClick={handleBulkTier} disabled={bulkSaving}>
              {bulkSaving ? `Đang xử lý ${selectedIds.size}...` : `✓ Áp dụng cho ${selectedIds.size} users`}
            </button>
          </>
        }
      >
        <div className="info-box" style={{ marginBottom: 16 }}>
          Sẽ đổi tier của <strong>{selectedIds.size}</strong> user đã chọn. Không thể hoàn tác.
        </div>
        <div className="form-group">
          <label className="form-label">Tier mới</label>
          <select className="input" value={bulkTier} onChange={e => setBulkTier(e.target.value as SubscriptionTier)} style={{ width: '100%' }}>
            <option value="member">👤 Member</option>
            <option value="premium">💜 Premium</option>
          </select>
        </div>
        {bulkTier === 'premium' && (
          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label">⏳ Thời hạn Premium</label>
            <select className="input" value={bulkDays} onChange={e => setBulkDays(Number(e.target.value))} style={{ width: '100%' }}>
              {PREMIUM_DURATIONS.map(opt => <option key={opt.days} value={opt.days}>{opt.label}</option>)}
            </select>
          </div>
        )}
      </Modal>
    </div>
  );
}
